import React from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — copy it manually.");
    }
  };
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-text-muted">{label}</p>
      <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2">
        <code className="flex-1 break-all font-mono text-sm text-text-main" data-testid={`${testId}-value`}>{value}</code>
        <Button type="button" size="sm" variant="outline" onClick={copy} data-testid={`${testId}-copy`}>
          {copied ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
        </Button>
      </div>
    </div>
  );
}

/**
 * One-time credential confirmation shown after creating an admin or resetting a
 * password. The plaintext password is available ONLY here (never stored
 * readable, never returned again).
 */
export function CredentialDialog({ open, onOpenChange, username, password, title }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-credential-dialog">
        <DialogHeader>
          <DialogTitle>{title || "Admin credentials"}</DialogTitle>
          <DialogDescription>
            Copy these now — the password is shown only once and can't be retrieved later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {username && <CopyRow label="Username / Login ID" value={username} testId="cred-username" />}
          {password && <CopyRow label="Temporary Password" value={password} testId="cred-password" />}
        </div>
        <DialogFooter>
          <Button variant="accent" onClick={() => onOpenChange(false)} data-testid="admin-credential-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
